import type {
  HomeownerUser,
  LoginResponseData,
  VerifyEmailResponseData,
} from "@/src/types/auth.types";

function resolveUserId(data: LoginResponseData): string {
  return data.userId ?? data.id ?? "";
}

export function mapUserFromAuthData(
  data: LoginResponseData,
  emailVerified: boolean,
): HomeownerUser | null {
  const id = resolveUserId(data);
  if (!id && !data.email) return null;

  const resolvedId = id || data.email || "";

  return {
    id: resolvedId,
    userId: resolvedId,
    firstName: data.firstName ?? "",
    lastName: data.lastName ?? "",
    email: data.email ?? "",
    phoneNumber: data.phoneNumber ?? "",
    phoneCountryCode: data.phoneCountryCode ?? null,
    phoneCountryIsoCode: data.phoneCountryIsoCode ?? null,
    phone: data.phone ?? null,
    propertyAddress: data.propertyAddress ?? "",
    emailVerifiedAt:
      typeof data.emailVerifiedAt === "string" ? data.emailVerifiedAt : null,
    emailVerified,
    status: data.status ?? "ACTIVE",
  };
}

function hasFullSession(data: LoginResponseData): boolean {
  return Boolean(
    data.accessToken && data.refreshToken && resolveUserId(data) && data.email,
  );
}

export function mapLoginResponse(data: LoginResponseData): {
  user: HomeownerUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  requiresVerification: boolean;
  pendingUserId: string | null;
  pendingEmail: string | null;
} {
  const isVerified = data.emailVerified === true;
  const requiresVerification = !isVerified;
  const pendingUserId = resolveUserId(data) || null;
  const pendingEmail = data.email ?? null;
  const user = mapUserFromAuthData(data, isVerified);

  if (requiresVerification) {
    return {
      user,
      accessToken: null,
      refreshToken: null,
      requiresVerification: true,
      pendingUserId,
      pendingEmail,
    };
  }

  if (!hasFullSession(data) || !user) {
    return {
      user: null,
      accessToken: null,
      refreshToken: null,
      requiresVerification: false,
      pendingUserId: null,
      pendingEmail: null,
    };
  }

  return {
    user,
    accessToken: data.accessToken ?? null,
    refreshToken: data.refreshToken ?? null,
    requiresVerification: false,
    pendingUserId: null,
    pendingEmail: null,
  };
}

export function mapVerifyEmailResponse(data: VerifyEmailResponseData): {
  user: HomeownerUser;
  accessToken: string;
  refreshToken: string;
} {
  const user = mapUserFromAuthData(data, true);
  if (!user || !data.accessToken || !data.refreshToken) {
    throw new Error("Invalid verify-email response");
  }

  return {
    user: { ...user, emailVerified: true },
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };
}
